import axiosInstance from "core/api/axiosInstance";
import * as XLSX from "xlsx";

/**
 * Formatea una fecha para mostrar
 */
const formatDate = (dateString) => {
  if (!dateString) return new Date().toLocaleString();
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return new Date().toLocaleString();
  return date.toLocaleString("es-ES", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

/**
 * Normaliza un registro para la UI
 * Mapea los campos del Excel: plate, commerce, commerce_ip, name, last_name, phone
 */
export const normalizeRecord = (record) => {
  return {
    id: record.id || "N/A",
    placa: record.placa || record.plate || record.PLACA || "N/A",
    comercio: record.comercio || record.commerce || record.COMMERCE || "N/A",
    ipComercio: record.ipComercio || record.commerce_ip || record.COMMERCE_IP || "N/A",
    nombre: record.nombre || record.name || record.NAME || "N/A",
    apellido: record.apellido || record.last_name || record.LAST_NAME || "N/A",
    telefono: record.telefono || record.phone || record.PHONE || "N/A",
    createdAt: record.createdAt || record.created_at || new Date().toISOString(),
    createdAtFormatted: formatDate(record.createdAt || record.created_at),
    updatedAt: record.updatedAt || record.updated_at,
    updatedAtFormatted: formatDate(record.updatedAt || record.updated_at),
    rawData: record,
  };
};

/**
 * Lee un archivo Excel y devuelve los datos como JSON
 */
const readExcelFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);
        resolve(jsonData);
      } catch (error) {
        reject(new Error("Error al leer el archivo Excel: " + error.message));
      }
    };

    reader.onerror = () => {
      reject(new Error("Error al leer el archivo"));
    };

    reader.readAsArrayBuffer(file);
  });
};

/**
 * Valida un archivo Excel de registros antes de importar
 * Campos esperados: plate, commerce, commerce_ip, name, last_name, phone
 * Solo valida duplicados dentro del archivo
 */
export const validateFile = async (file) => {
  try {
    if (!file) {
      return {
        isValid: false,
        errors: ["No se ha seleccionado ningún archivo"],
        duplicateInFile: [],
        duplicateInDatabase: [],
        data: [],
      };
    }

    const fileExtension = file.name.split(".").pop().toLowerCase();
    if (fileExtension !== "xlsx" && fileExtension !== "xls") {
      return {
        isValid: false,
        errors: ["Formato de archivo inválido. Solo se permiten archivos .xlsx o .xls"],
        duplicateInFile: [],
        duplicateInDatabase: [],
        data: [],
      };
    }

    const maxSize = 40 * 1024 * 1024;
    if (file.size > maxSize) {
      return {
        isValid: false,
        errors: ["El archivo es demasiado grande. Máximo 40MB permitidos"],
        duplicateInFile: [],
        duplicateInDatabase: [],
        data: [],
      };
    }

    const data = await readExcelFile(file);

    if (!data || data.length === 0) {
      return {
        isValid: false,
        errors: ["El archivo no contiene datos válidos"],
        duplicateInFile: [],
        duplicateInDatabase: [],
        data: [],
      };
    }

    // Columnas requeridas
    const expectedHeaders = {
      PLATE: ["plate", "PLATE", "Placa", "placa", "PLACA"],
      COMMERCE: ["commerce", "COMMERCE", "Comercio", "comercio"],
      COMMERCE_IP: ["commerce_ip", "COMMERCE_IP", "commerceIp", "ipComercio"],
      NAME: ["name", "NAME", "Nombre", "nombre", "NOMBRE"],
      LAST_NAME: ["last_name", "LAST_NAME", "lastName", "Apellido", "apellido", "APELLIDO"],
      PHONE: ["phone", "PHONE", "Telefono", "teléfono", "TELEFONO"],
    };

    const headers = Object.keys(data[0]);
    const foundHeaders = {};
    const missingHeaders = [];

    for (const [required, variations] of Object.entries(expectedHeaders)) {
      const found = variations.find((v) => headers.includes(v));
      if (found) {
        foundHeaders[required] = found;
      } else {
        missingHeaders.push(required);
      }
    }

    if (missingHeaders.length > 0) {
      return {
        isValid: false,
        errors: [`Faltan columnas requeridas: ${missingHeaders.join(", ")}`],
        duplicateInFile: [],
        duplicateInDatabase: [],
        data: [],
      };
    }

    const errors = [];
    const validRows = [];
    const platesInFile = new Map();
    const duplicateInFile = [];

    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      const rowNumber = i + 2;
      const rowErrors = [];

      const plate = row[foundHeaders["PLATE"]]?.toString().trim() || "";
      const commerce = row[foundHeaders["COMMERCE"]]?.toString().trim() || "";
      const commerce_ip = row[foundHeaders["COMMERCE_IP"]]?.toString().trim() || "";
      const name = row[foundHeaders["NAME"]]?.toString().trim() || "";
      const last_name = row[foundHeaders["LAST_NAME"]]?.toString().trim() || "";
      const phone = row[foundHeaders["PHONE"]]?.toString().trim() || "";

      // Validar campos obligatorios
      if (!plate) rowErrors.push("PLATE (Placa) está vacío");
      if (!commerce) rowErrors.push("COMMERCE (Comercio) está vacío");
      if (!commerce_ip) rowErrors.push("COMMERCE_IP (IP Comercio) está vacío");
      if (!name) rowErrors.push("NAME (Nombre) está vacío");
      if (!last_name) rowErrors.push("LAST_NAME (Apellido) está vacío");
      if (!phone) rowErrors.push("PHONE (Teléfono) está vacío");

      // Validar que el teléfono sea numérico
      if (phone && !/^\d+$/.test(phone)) {
        rowErrors.push("PHONE (Teléfono) debe contener solo números");
      }

      // Validar placa única en el archivo
      if (plate) {
        const plateKey = plate.toLowerCase();
        if (platesInFile.has(plateKey)) {
          duplicateInFile.push({
            row: rowNumber,
            type: "PLATE",
            value: plate,
            firstOccurrence: platesInFile.get(plateKey),
          });
          rowErrors.push(
            `PLATE (Placa) duplicada en el archivo (primera vez en fila ${platesInFile.get(
              plateKey
            )})`
          );
        } else {
          platesInFile.set(plateKey, rowNumber);
        }
      }

      if (rowErrors.length > 0) {
        errors.push({
          row: rowNumber,
          errors: rowErrors,
          data: row,
        });
      } else {
        validRows.push({
          plate,
          commerce,
          commerce_ip,
          name,
          last_name,
          phone,
          originalRow: rowNumber,
        });
      }
    }

    const finalValidRows = validRows.filter((row) => {
      const hasError = errors.some((err) => err.row === row.originalRow);
      return !hasError;
    });

    const isValid = errors.length === 0;
    const formattedErrors = errors.map((e) => `Fila ${e.row}: ${e.errors.join(", ")}`);

    return {
      isValid,
      errors: formattedErrors,
      duplicateInFile: duplicateInFile,
      duplicateInDatabase: [],
      validRows: finalValidRows,
      totalRows: data.length,
      validCount: finalValidRows.length,
      invalidCount: errors.length,
      data: finalValidRows,
    };
  } catch (error) {
    console.error("❌ Error al validar archivo:", error);
    return {
      isValid: false,
      errors: [error.message || "Error al validar el archivo"],
      duplicateInFile: [],
      duplicateInDatabase: [],
      data: [],
    };
  }
};

/**
 * Importa un archivo Excel con registros
 * Endpoint: POST /api/tp/import-vicidial
 */
export const importRecords = async (file, onProgress) => {
  try {
    if (!file) {
      return {
        success: false,
        error: "No se ha seleccionado ningún archivo",
      };
    }

    if (onProgress) onProgress(20);

    const validation = await validateFile(file);

    if (onProgress) onProgress(40);

    if (!validation.isValid) {
      return {
        success: false,
        error: "El archivo contiene errores de validación",
        validationErrors: validation.errors,
        duplicateInFile: validation.duplicateInFile,
        duplicateInDatabase: validation.duplicateInDatabase,
      };
    }

    if (validation.validRows.length === 0) {
      return {
        success: false,
        error: "No hay filas válidas para importar",
      };
    }

    if (onProgress) onProgress(60);

    const formData = new FormData();
    formData.append("file", file);

    const { data } = await axiosInstance.post("/api/tp/import-vicidial", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: 60000,
    });

    if (onProgress) onProgress(100);

    if (!data || typeof data !== "object") {
      return {
        success: false,
        error: "Respuesta inválida del servidor",
      };
    }

    const isSuccess =
      data.success === true || data.status === "success" || data.msm === "succesful";
    const importedCount = data.importedCount || data.count || validation.validRows.length || 0;

    return {
      success: isSuccess,
      message: data.msm || data.message || `Se importaron ${importedCount} registros exitosamente`,
      data: data.data || validation.validRows,
      importedCount: importedCount,
      validationSummary: {
        totalRows: validation.totalRows,
        validRows: validation.validCount,
        invalidRows: validation.invalidCount,
        duplicateInFile: validation.duplicateInFile.length,
        duplicateInDatabase: validation.duplicateInDatabase.length,
      },
      rawResponse: data,
    };
  } catch (error) {
    console.error("❌ Error al importar registros:", error);

    if (onProgress) onProgress(0);

    let errorMessage = "Error al importar el archivo";

    if (error.response) {
      const serverMessage =
        error.response.data?.msm || error.response.data?.message || error.response.data?.error;

      if (serverMessage) {
        errorMessage = serverMessage;
      }

      if (error.response.status === 404) {
        errorMessage = "El endpoint de importación no está disponible";
      } else if (error.response.status === 413) {
        errorMessage = "El archivo es demasiado grande para el servidor";
      } else if (error.response.status === 400) {
        errorMessage = error.response.data?.msm || "Archivo inválido o formato incorrecto";
      } else if (error.response.status === 401) {
        errorMessage = "No autorizado. Por favor, inicie sesión nuevamente.";
      } else if (error.response.status === 500) {
        errorMessage = "Error interno del servidor";
      }
    } else if (error.request) {
      errorMessage = "No se pudo conectar con el servidor";
    } else if (error.code === "ECONNABORTED") {
      errorMessage = "La solicitud ha excedido el tiempo de espera";
    }

    return {
      success: false,
      error: errorMessage,
      importedCount: 0,
    };
  }
};

// Exportar solo lo que realmente se usa
export default {
  normalizeRecord,
  validateFile,
  importRecords,
};
